import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Phone, MessageCircle, ChevronDown } from "lucide-react";
import { COMPANY, LOGO_URL, SERVICES, TRUST_BADGES, TARGET_GROUPS, PROMISES } from "@/data";
import { Reveal, MaskLine } from "@/components/motion";
import Icon from "@/components/Icon";
import ContactForm from "@/components/ContactForm";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] flex items-center overflow-hidden bg-ink grain">
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1670589953882-b94c9cb380f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 pt-28 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="mb-8"
        >
          <img src={LOGO_URL} alt="M&A Objektservice Logo" className="h-24 w-24 md:h-28 md:w-28 rounded-full object-cover ring-1 ring-gold/40 shadow-[0_0_60px_rgba(201,169,97,0.25)]" />
        </motion.div>

        <p className="overline text-gold text-xs md:text-sm uppercase tracking-[0.3em] mb-5 font-sans">
          <MaskLine delay={0.2}>Alles rund um Ihr Objekt</MaskLine>
        </p>

        <h1 className="font-serif text-5xl sm:text-6xl md:text-8xl leading-[0.95] tracking-tight text-white max-w-4xl">
          <MaskLine delay={0.3}>Zuverlässig.</MaskLine>
          <MaskLine delay={0.42} className="text-gold-gradient">Sauber.</MaskLine>
          <MaskLine delay={0.54}>Professionell.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-lg md:text-xl text-white/60 font-light leading-relaxed"
        >
          Ihr Partner für Hausmeisterservice, Reinigung, Gartenpflege und mehr –
          in Gelnhausen und 50 km Umkreis.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/kontakt"
            data-testid="hero-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-ink font-medium transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_40px_rgba(201,169,97,0.45)]"
          >
            Kostenlose Besichtigung anfragen
            <ArrowUpRight size={20} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a
            href={COMPANY.whatsapp}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 text-white transition-all duration-300 hover:border-gold hover:text-gold"
          >
            <MessageCircle size={20} /> WhatsApp
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/40"
      >
        <ChevronDown size={26} />
      </motion.div>
    </section>
  );
}

function TrustBar() {
  return (
    <section data-testid="trust-bar" className="bg-ink border-t border-white/5 py-8">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {TRUST_BADGES.map((b, i) => (
          <Reveal key={b.label} delay={i * 0.08}>
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-full border border-gold/30 text-gold shrink-0">
                <Icon name={b.icon} size={18} />
              </span>
              <span className="text-sm text-white/80">{b.label}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function EditorialMarquee() {
  return (
    <div className="bg-ink py-6 border-y border-white/5">
      <Marquee speed={40} gradient={false} autoFill>
        {["ZUVERLÄSSIG", "SAUBER", "PROFESSIONELL", "ALLES RUND UM IHR OBJEKT"].map((t) => (
          <span key={t} className="font-serif italic text-3xl md:text-5xl text-white/15 mx-8 flex items-center gap-8">
            {t} <span className="text-gold not-italic">·</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Intro() {
  return (
    <section data-testid="intro" className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-4">
          <span className="overline text-xs uppercase tracking-[0.2em] text-gold-dark font-medium">Über uns</span>
        </div>
        <div className="md:col-span-8">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-ink">
              Ein Ansprechpartner für <span className="text-gold-dark italic">alles</span>, was Ihr Objekt braucht.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg text-neutral-600 font-light leading-relaxed max-w-2xl">
              M&amp;A Objektservice steht für gepflegte Immobilien und zufriedene Eigentümer.
              Von der wöchentlichen Treppenhausreinigung über Gartenpflege und Winterdienst bis zur
              kompletten Entrümpelung – wir kümmern uns zuverlässig, sauber und professionell.
              Fordern Sie jetzt Ihre <strong className="text-ink font-medium">kostenlose Besichtigung</strong> an.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ServicesBento() {
  return (
    <section data-testid="services-preview" className="bg-cream pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <Reveal>
            <span className="overline text-xs uppercase tracking-[0.2em] text-gold-dark font-medium">Leistungen</span>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-ink">Unser Angebot</h2>
          </Reveal>
          <Link to="/leistungen" className="group inline-flex items-center gap-2 text-ink hover:text-gold-dark transition-colors">
            Alle Leistungen ansehen
            <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5 auto-rows-[220px]">
          {SERVICES.slice(0, 6).map((s, i) => {
            const featured = i === 0 || i === 3;
            return (
              <Reveal
                key={s.slug}
                delay={(i % 3) * 0.08}
                className={featured ? "md:col-span-2 md:row-span-2" : "md:row-span-1"}
              >
                <Link
                  to="/leistungen"
                  className="group relative block h-full w-full overflow-hidden rounded-2xl bg-ink"
                >
                  <img
                    src={s.image}
                    alt={s.title}
                    className="absolute inset-0 h-full w-full object-cover opacity-45 transition-all duration-700 group-hover:opacity-60 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                  <div className="relative z-10 flex h-full flex-col justify-between p-6">
                    <span className="grid h-11 w-11 place-items-center rounded-full bg-gold/15 text-gold backdrop-blur">
                      <Icon name={s.icon} size={20} />
                    </span>
                    <div>
                      <h3 className={`font-serif text-white ${featured ? "text-3xl" : "text-xl"}`}>{s.title}</h3>
                      {featured && <p className="mt-2 text-white/60 text-sm max-w-xs">{s.desc}</p>}
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section data-testid="manifesto" className="bg-ink text-white grain py-24 md:py-32">
      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <span className="overline text-xs uppercase tracking-[0.2em] text-gold font-medium">Unser Versprechen</span>
          <h2 className="mt-3 font-serif text-4xl md:text-6xl tracking-tight max-w-3xl">
            Drei Prinzipien, auf die Sie sich verlassen können.
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-12 md:gap-8">
          {PROMISES.map((p, i) => (
            <Reveal key={p.no} delay={i * 0.1}>
              <div className="grid md:grid-cols-12 gap-4 md:gap-8 items-baseline border-t border-white/10 pt-8">
                <span className="md:col-span-3 font-serif italic text-6xl md:text-7xl text-gold-gradient">{p.no}</span>
                <h3 className="md:col-span-3 font-serif text-3xl">{p.title}</h3>
                <p className="md:col-span-6 text-white/55 font-light text-lg leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TargetGroups() {
  return (
    <section data-testid="target-groups" className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <span className="overline text-xs uppercase tracking-[0.2em] text-gold-dark font-medium">Für wen wir arbeiten</span>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-2xl">Von privat bis gewerblich – wir sind für Sie da.</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-5">
          {TARGET_GROUPS.map((g, i) => (
            <Reveal key={g.label} delay={(i % 3) * 0.08}>
              <div className="hover-lift h-full rounded-2xl border border-black/5 bg-white p-7">
                <span className="grid h-12 w-12 place-items-center rounded-full bg-ink text-gold mb-5">
                  <Icon name={g.icon} size={20} />
                </span>
                <h3 className="font-serif text-xl text-ink">{g.label}</h3>
                <p className="mt-1.5 text-sm text-neutral-500">{g.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceArea() {
  return (
    <section data-testid="service-area" className="bg-cream pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid md:grid-cols-2 gap-10 items-center">
        <Reveal>
          <span className="overline text-xs uppercase tracking-[0.2em] text-gold-dark font-medium">Einsatzgebiet</span>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-ink">Gelnhausen <span className="text-gold-dark">+ 50 km</span> Umkreis</h2>
          <p className="mt-6 text-lg text-neutral-600 font-light leading-relaxed max-w-md">
            Wir sind in Gelnhausen zuhause und im gesamten Umkreis von 50 km für Sie im Einsatz –
            schnell vor Ort, wann immer Sie uns brauchen.
          </p>
          <p className="mt-6 text-sm text-neutral-500">{COMPANY.address}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="overflow-hidden rounded-2xl border border-black/5 shadow-lg">
            <iframe
              title="Einsatzgebiet Gelnhausen"
              src="https://www.google.com/maps?q=Gelnhausen&z=10&output=embed"
              className="h-[380px] w-full grayscale-[0.2]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ContactCTA() {
  return (
    <section id="kontakt" data-testid="home-contact" className="bg-ink text-white grain py-24 md:py-32">
      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 grid md:grid-cols-2 gap-14">
        <div>
          <Reveal>
            <span className="overline text-xs uppercase tracking-[0.2em] text-gold font-medium">Kostenlose Besichtigung</span>
            <h2 className="mt-3 font-serif text-4xl md:text-6xl tracking-tight leading-tight">
              Lassen Sie uns Ihr Objekt <span className="text-gold-gradient italic">ansehen.</span>
            </h2>
            <p className="mt-6 text-white/55 font-light text-lg max-w-md">
              Unverbindlich & kostenlos. Schildern Sie uns kurz Ihr Anliegen – wir melden uns schnell mit einem fairen Preis.
            </p>
          </Reveal>
          <div className="mt-10 space-y-4">
            <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-4 text-white/80 hover:text-gold transition-colors">
              <span className="grid h-11 w-11 place-items-center rounded-full border border-gold/30 text-gold"><Phone size={18} /></span>
              {COMPANY.phone}
            </a>
            <a href={COMPANY.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-4 text-white/80 hover:text-gold transition-colors">
              <span className="grid h-11 w-11 place-items-center rounded-full border border-gold/30 text-gold"><MessageCircle size={18} /></span>
              WhatsApp Nachricht
            </a>
          </div>
        </div>
        <Reveal delay={0.15}>
          <ContactForm />
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <TrustBar />
      <EditorialMarquee />
      <Intro />
      <ServicesBento />
      <Manifesto />
      <TargetGroups />
      <ServiceArea />
      <ContactCTA />
    </main>
  );
}
